/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: dynamicDestinationsActions.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the PBX Applications module.
────────────────────────────────────────────────────────────────*/

import type { NavigateFunction } from 'react-router-dom';
import { buildApiUrl, getApiHeaders } from '../../config/api';

/**
 * Shared helpers for Dynamic Destination module actions (used in Grid + Form views).
 */

/**
 * Build the URL used to duplicate a Dynamic Destination entry in the form.
 * Pattern: /pbx/applications/dynamic-destination/new?duplicate={id}
 */
export const getDynamicDestinationDuplicateUrl = (id: string | number): string =>
  `/pbx/applications/dynamic-destination/new?duplicate=${id}`;

/**
 * Navigate to the form in duplicate mode for the given Dynamic Destination id.
 */
export const navigateToDynamicDestinationDuplicate = (
  navigate: NavigateFunction,
  id: string | number,
): void => {
  navigate(getDynamicDestinationDuplicateUrl(id));
};

/**
 * Low-level API helper to delete a Dynamic Destination entry by id.
 * UI components are responsible for showing notifications and handling navigation/refresh.
 */
export const deleteDynamicDestinationById = async (id: string | number): Promise<void> => {
  const response = await fetch(
    buildApiUrl(`/api/telephony/dynamic-destinations/${id}`),
    {
      method: 'DELETE',
      headers: getApiHeaders(),
    },
  );

  if (!response.ok) {
    let message = 'Failed to delete dynamic destination';
    try {
      const errorData = await response.json();
      if (errorData?.message) {
        message = errorData.message;
      }
    } catch {
      // Ignore JSON parse errors and use default message
    }
    throw new Error(message);
  }
};



